import { isRegisterableAccelerator } from '@electron/shared/keyboard-accelerator';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

import { electronApis } from '@/features/electron-api';
import {
  findShortcutConflict,
  getShortcutDefinition,
  SHORTCUT_DEFINITIONS,
  type ShortcutId,
} from '@/features/shortcuts';
import { settingsStore } from '@/stores/settings.store';

// The recorder uses DOM key names; the native API stores Electron accelerators
const acceleratorKeys: Record<string, string> = {
  ctrl: 'control',
  alt: 'option',
  meta: 'command',
  comma: ',',
  period: '.',
  slash: '/',
  backslash: '\\',
  semicolon: ';',
  quote: "'",
  backquote: '`',
  minus: '-',
  equal: '=',
  bracketleft: '[',
  bracketright: ']',
};

export function useSaveMeetingWidgetShortcut() {
  const saving = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  const saveShortcut = useCallback(async (combo: string): Promise<boolean> => {
    if (saving.current) return false;
    const shortcut = combo
      .split('+')
      .map((key) => acceleratorKeys[key] ?? key)
      .join('+');
    if (shortcut) {
      if (!isRegisterableAccelerator(shortcut)) {
        toast.error('Choose a shortcut with at least one modifier and a key.');
        return false;
      }
      for (const id of Object.keys(SHORTCUT_DEFINITIONS) as ShortcutId[]) {
        const conflict = findShortcutConflict(
          shortcut,
          id,
          (shortcutId) => settingsStore.getShortcutCombo(shortcutId),
          (shortcutId) => settingsStore.isShortcutEnabled(shortcutId)
        );
        if (conflict) {
          toast.error(
            `${getShortcutDefinition(conflict).label} already uses that shortcut.`
          );
          return false;
        }
      }
    }

    saving.current = true;
    setIsSaving(true);
    try {
      const result =
        await electronApis.meetings.setMeetingWidgetShortcut(shortcut);
      if (!result.success) {
        toast.error(
          result.reason === 'invalid'
            ? 'Choose a valid shortcut.'
            : result.reason === 'unavailable'
              ? 'That shortcut is in use by the system or another app. Try another.'
              : "Littlebird couldn't register that shortcut. Your previous shortcut is still active."
        );
        return false;
      }
      settingsStore.setMeetingWidgetShortcut(result.shortcut);
      return true;
    } catch {
      toast.error("Couldn't save the meeting widget shortcut.");
      return false;
    } finally {
      saving.current = false;
      setIsSaving(false);
    }
  }, []);

  return { saveShortcut, isSaving };
}
